import React, { useMemo } from 'react';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import BigNumber from 'bignumber.js';

import Section from 'app/components/common/Section';
import InfoTab from 'app/components/ui/InfoTab';
import { AppRoutes } from 'app/static/routes/routes-enum';
import Graphic from 'app/components/graphs/Graphic';
import TokensTable from 'app/components/tables/TokensTable';
import PairsTable from 'app/components/tables/PairsTable';
import TransactionsTable from 'app/components/tables/TransactionsTable';
import { GraphicTypes } from 'app/components/graphs/types';
import {
  useGetTokensLazyQuery,
  useInfoTabsQuery,
  useGetTransactionLazyQuery,
  useGetPairsLazyQuery,
} from 'app/generated/graphql';
import { InfoTabsDataType } from 'app/types/info-tabs';
import { prettyPrice, transformMutezToTez } from 'app/utils/helpers';
import { useCurrency } from 'app/context/CurrencyContext';
import { TransactionsScopeEnum } from 'app/types/transactions';

import s from './Overview.module.sass';

const Analytics: React.FC = () => {
  const { currency } = useCurrency();

  const { data, loading } = useInfoTabsQuery();

  const [fetchTokensData, { data: tokensData }] = useGetTokensLazyQuery();

  const [fetchPairsData, { data: pairsData }] = useGetPairsLazyQuery();

  const [fetchCurrentType, { data: transactionData }] = useGetTransactionLazyQuery();

  const infoTabsData = useMemo(() => [
    {
      id: 1,
      title: 'Tezos USD price:',
      value: data ? prettyPrice(+data.overview.xtzUsdQuote, 3) : 0,
      change: {
        day: data ? data.overview.xtzUsdQuoteChange.day : 0,
        week: data ? data.overview.xtzUsdQuoteChange.week : 0,
        month: data ? data.overview.xtzUsdQuoteChange.month : 0,
      },
    },
    {
      id: 2,
      title: 'Total liquidity:',
      value: data ? transformMutezToTez(
        {
          value: +data.overview.totalLiquidity!,
          fraction: 3,
          currency,
          xtzUsdQuote: +data?.overview.xtzUsdQuote,
        },
      ) : 0,
      change: {
        day: data ? data.overview.totalLiquidityChange.day : 0,
        week: data ? data.overview.totalLiquidityChange.week : 0,
        month: data ? data.overview.totalLiquidityChange.month : 0,
      },
      xtzOnly: currency === 'USD',
    },
    {
      id: 3,
      title: 'Volume (24h):',
      value: data ? transformMutezToTez({
        value: +data.overview.volume24h!,
        fraction: 3,
        currency,
        xtzUsdQuote: +data?.overview.xtzUsdQuote,
      }) : 0,
      change: {
        day: data ? data.overview.volumeChange.day : 0,
        week: data ? data.overview.volumeChange.week : 0,
        month: data ? data.overview.volumeChange.month : 0,
      },
      xtzOnly: currency === 'USD',
    },
    {
      id: 4,
      title: 'Transactions (24h):',
      value: data ? data.overview.trasactionsCount24h : 0,
    },
  ] as InfoTabsDataType[], [currency, data]);

  const graphicLiquidityData = useMemo(() => {
    const oldData = data ? [...data.overview.plotLiquidity] : undefined;
    if (oldData && data && data.overview.totalLiquidity) {
      oldData.push({
        time: Date.now() / 1e3,
        value: new BigNumber(data.overview.totalLiquidity).div(1e6).toNumber(),
        xtzUsdQuoteHistorical: data.overview.xtzUsdQuote,
      });
      return oldData;
    }
    return undefined;
  }, [data]);

  const graphicVolumeData = useMemo(() => {
    const oldData = data ? [...data.overview.plotVolume] : undefined;
    if (oldData && data && data.overview.volume24h) {
      oldData.push({
        time: Date.now() / 1e3,
        value: new BigNumber(data.overview.volume24h).div(1e6).toNumber(),
        xtzUsdQuoteHistorical: data.overview.xtzUsdQuote,
      });
      return oldData;
    }
    return undefined;
  }, [data]);

  return (
    <>
      <Helmet>
        <title>QuipuSwap Statistic</title>
        <meta
          name="description"
          content="View analytical data for QuipuSwap exchange: prices,
          liquidity, dynamics, trading volume, and much more. Check Now."
        />
      </Helmet>

      <div className={cx(s.root)}>
        <Section title="Overview" head>
          <div className={cx(s.tabs)}>
            {infoTabsData.map(({
              id, title, value, change, xtzOnly,
            }) => (
              <InfoTab
                key={id}
                title={title}
                value={value}
                change={change}
                className={cx(s.infotab)}
                currency={currency}
                xtzOnly={xtzOnly}
                loading={loading}
              />
            ))}
          </div>

          <div className={cx(s.graphWrapper)}>
            <Graphic
              data={graphicLiquidityData}
              loading={loading}
              title="Liquidity"
              className={cx(s.graph)}
              height={300}
            />
            <Graphic
              data={graphicVolumeData}
              loading={loading}
              title="Volume"
              theme={GraphicTypes.Histogram}
              className={cx(s.graph)}
              height={300}
            />
          </div>
        </Section>

        <Section title="Tokens" to={AppRoutes.TOKENS}>
          <TokensTable
            data={tokensData}
            fetch={fetchTokensData}
            pageSize={10}
          />
        </Section>

        <Section title="Pairs" to={AppRoutes.PAIRS}>
          <PairsTable
            data={pairsData}
            fetch={fetchPairsData}
            pageSize={10}
          />
        </Section>

        <Section title="Transactions">
          <TransactionsTable
            data={transactionData}
            fetchCurrentType={fetchCurrentType}
            scope={TransactionsScopeEnum.OVERVIEW}
          />
        </Section>
      </div>
    </>
  );
};
export default Analytics;
